import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function LoginPage() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left — editorial side */}
      <div className="hidden lg:flex relative overflow-hidden flex-col justify-between p-12 bg-zinc-950 text-zinc-100">
        <div className="absolute inset-0 opacity-[0.06] grid-lines pointer-events-none" />
        <div className="relative">
          <div className="overline text-zinc-400 mb-3">Prism · inbox intelligence</div>
          <div className="font-serif text-6xl tracking-tight leading-[1.02]">
            Prism
          </div>
          <div className="font-serif italic text-2xl text-zinc-400 mt-3 leading-snug">
            Refract your inbox<br />into revenue.
          </div>
        </div>
        <div className="relative max-w-sm">
          <div className="h-px w-16 bg-zinc-700 mb-4" />
          <p className="text-sm text-zinc-400 leading-relaxed">
            Audit every email the Gmail → Salesforce pipeline processes.
            Trace each Opportunity field back to its source. Quantify pipeline
            dollars. One pane of glass for Sales Ops, CRM admins, and execs.
          </p>
          <div className="mt-6 grid grid-cols-3 gap-4 font-mono text-[11px] uppercase tracking-widest text-zinc-500">
            <div>Visibility</div>
            <div>Audit</div>
            <div>ROI</div>
          </div>
          <div className="mt-10 flex items-center gap-3">
            <span className="text-[9px] uppercase tracking-[0.2em] text-zinc-600">Powered by</span>
            <img
              src="https://customer-assets.emergentagent.com/job_metrics-hub-190/artifacts/fg8v3tuh_rtslogo-with-name-transparent-300-ppi-RGB.png"
              alt="Resolve Tech Solutions"
              className="h-8 w-auto object-contain brightness-0 invert opacity-80"
            />
          </div>
        </div>
      </div>

      {/* Right — form */}
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="overline mb-2">Sign in</div>
            <h1 className="font-serif text-3xl tracking-tight">Prism</h1>
            <p className="text-sm text-muted-foreground mt-1 italic font-serif">
              Refract your inbox into revenue.
            </p>
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="email" className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 font-mono text-sm"
                autoComplete="email"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 font-mono text-sm"
                autoComplete="current-password"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="login-submit-btn"
            disabled={busy}
            className="w-full h-10 bg-zinc-900 hover:bg-zinc-800 text-white rounded-md"
          >
            {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
            Sign in
          </Button>
        </form>
      </div>
    </div>
  );
}
